import { BooleanInput, SelectInput, TextInput, required, useGetIdentity, useTranslate } from 'react-admin';
import { Box, Stack, Button, Typography, Divider } from '@mui/material';
import { useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { Iconify } from 'src/components/iconify';
import { UserModel } from 'src/pages/admin/entities/user.entity';

const PATH_PREFIX_CHOICES = [
    { id: 'apk', name: 'apk' },
    { id: 'banners', name: 'banners' },
    { id: 'documents', name: 'documents' },
    { id: 'images', name: 'images' },
    { id: 'videos', name: 'videos' },
    { id: 'xp', name: 'xp' },
    { id: 'test', name: 'test' },
];

const ADMIN_ONLY_CHOICE = {id: 'releases', name: 'releases'};
const APK_PREFIXES = ['apk', 'releases'];
const ADMIN_EMAIL = process.env.REACT_APP_CDN_ADMIN_EMAIL;

const computeSha256Short = async (file: File | undefined): Promise<string> => {
    if(!file) return ''
    const buffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
    return hashHex.slice(0, 6);
};

const CdnUploadForm = ({onHashingChange, isHashing}: {onHashingChange: (v: boolean) => void, isHashing: boolean}) => {
    const t = useTranslate();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const folderInputRef = useRef<HTMLInputElement>(null);
    const { setValue, watch, getValues } = useFormContext();
    const selectedFile: File | undefined = watch('_file');
    const selectedFolderFiles: File[] | undefined = watch('_folderFiles');
    const pathPrefix: string = watch('pathPrefix');
    const { data: userData } = useGetIdentity();
    const user = userData as UserModel;

    const pathPrefixChoices = useMemo(() => {
        if (user?.email === ADMIN_EMAIL) {
            return [...PATH_PREFIX_CHOICES, ADMIN_ONLY_CHOICE];
        }
        return PATH_PREFIX_CHOICES;
    }, [user?.email]);
    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setValue('_file', file);
            setValue('_folderFiles', undefined); // clear folder mode
        }
        e.target.value = '';

        if (APK_PREFIXES.includes(pathPrefix)) {
            onHashingChange(true);
            setValue('_hashing', true);
            try {
                const shortHash = await computeSha256Short(file);
                const ext = file?.name.includes('.') ? `.${file.name.split('.').pop()}` : ''
                const baseName = file?.name.replace(/\.[^.]+$/, '');
                setValue('customFilename', `${baseName}_${shortHash}${ext}`);
            } finally {
                onHashingChange(false);
                setValue('_hashing', false);
            }
        } else {
            setValue('customFilename', `${file?.name}`)
        }
    };

    const handleFolderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        if (files.length > 0) {
            // Extract folder name from webkitRelativePath (e.g. "myFolder/a.png" → "myFolder")
            const folderName = files[0].webkitRelativePath?.split('/')?.[0] || '';
            setValue('_folderFiles', files);
            setValue('_folderName', folderName);
            setValue('_file', undefined); // clear single file mode
            setValue('customFilename', '');
        }
        e.target.value = '';
    };

    const handleFolderClick = () => {
        if (folderInputRef.current) {
            folderInputRef.current.setAttribute('webkitdirectory', '');
            folderInputRef.current.setAttribute('mozdirectory', '');
            folderInputRef.current.click();
        }
    };

    const folderTotalSize = selectedFolderFiles
        ? selectedFolderFiles.reduce((sum, f) => sum + f.size, 0) / 1024 / 1024
        : 0;

    return (
        <Box sx={{ pt: 2, pb: 1 }}>
            <Stack spacing={2}>
                <SelectInput
                    source="pathPrefix"
                    label="module.cdnUpload.pathPrefix"
                    validate={[required()]}
                    choices={pathPrefixChoices}
                    fullWidth
                    helperText="e.g. test-apk, banners, documents"
                />
                <TextInput
                    source="customFilename"
                    label="module.cdnUpload.customFilename"
                    fullWidth
                    disabled={!!(selectedFolderFiles && selectedFolderFiles.length > 0)}
                    helperText={selectedFolderFiles && selectedFolderFiles.length > 0 ? t('module.cdnUpload.customFilenameDisabledForFolder') : undefined}
                />
                <BooleanInput
                    source="usePrivateBucket"
                    label="module.cdnUpload.usePrivateBucket"
                    defaultValue={false}
                />

                {/* Single File Picker */}
                <Box>
                    <input ref={fileInputRef} type="file" hidden onChange={handleFileChange} />
                    <Button
                        variant="outlined"
                        onClick={() => fileInputRef.current?.click()}
                        startIcon={<Iconify icon="eva:attach-2-fill" />}
                        fullWidth
                        disabled={isHashing || !pathPrefix}
                    >
                        {isHashing ?  t('module.cdnUpload.hashing') : t('module.cdnUpload.selectFile')}
                    </Button>
                    {selectedFile && (
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                            <Iconify icon="eva:file-text-fill" sx={{ color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary" noWrap>
                                {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                            </Typography>
                        </Stack>
                    )}
                </Box>

                <Divider><Typography variant="caption" color="text.disabled">{t('module.cdnUpload.or')}</Typography></Divider>

                {/* Folder Picker */}
                <Box>
                    <input ref={folderInputRef} type="file" hidden multiple onChange={handleFolderChange} />
                    <Button
                        variant="outlined"
                        onClick={handleFolderClick}
                        startIcon={<Iconify icon="eva:folder-fill" />}
                        fullWidth
                        disabled={isHashing || !pathPrefix}
                        color="secondary"
                    >
                        {t('module.cdnUpload.selectFolder')}
                    </Button>
                    {selectedFolderFiles && selectedFolderFiles.length > 0 && (
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                            <Iconify icon="eva:folder-fill" sx={{ color: 'secondary.main', flexShrink: 0 }} />
                            <Typography variant="body2" color="text.secondary">
                                {t('module.cdnUpload.folderSelected', {
                                    count: selectedFolderFiles.length,
                                    size: folderTotalSize.toFixed(2),
                                })}
                            </Typography>
                        </Stack>
                    )}
                </Box>
            </Stack>
        </Box>
    );
};

export default CdnUploadForm;
